import { Fragment, useEffect, useState } from "react";
import { tss } from "tss-react/mui";
import { Typography, Button, TextField, Switch, Grid, Select, Menu, MenuItem, FormHelperText } from "@mui/material";
import { SketchPicker } from "react-color";
import * as GlobalActions from "actions/GlobalActions";
import { handleLayerEvent } from "actions/ViewerTools";
import ViewerEvents from "events/ViewerEvents";
import { Switcher, DashInput, Section, CustomSelect } from "components";
import { ArrowDropDown, HighlightOff } from "@mui/icons-material";
import commonStyles from "styles/common";

var useStyles = tss.create(({theme}) => ({
    sectionContainer: {
        borderTop: "solid 1px #ededed",
        lineHeight: 0,
    },
    arrow: {
        width: 16,
        height: 16,
        position: "relative",
        left: 4,
    },
    bottomButtonContainer: {
        marginBottom: 0,
        marginTop: 5,
        marginRight: 11,
        textAlign: "right",
    },
    listItem: {
        height: 40,
        display: "flex",
        alignItems: "center",
        "&:hover": {
            backgroundColor: "transparent",
        },
    },
    content: {
        padding: "2px 0px 8px 15px",
        boxShadow: "inset 0px 4px 3px 0px #0000000d",
        backgroundColor: "#f7f7f7",
    },
    label: {
        //color: "#434343",
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
    },
    row: {
        height: 35,
        display: "flex",
        alignItems: "center",
        width: "100%",
        position: "relative",
    },
    bigRow: {
        height: 45,
        display: "flex",
        alignItems: "center",
        width: "100%",
        position: "relative",
    },
    palette: {
        width: 220,
        height: 196,
        overflowY: "hidden",
        position: "relative",
        left: 2,
    },
    sliderInput: {
        display: "inline-block",
        width: 70,
        outline: 0,
        border: 0,
        textAlign: "right",
        color: theme.palette.text.secondary,
        padding: "5px 5px",
    },
    colorBox: {
        cursor: "pointer",
        width: 16,
        height: 16,
        display: "inline-block",
        backgroundColor: "#ccc",
        position: "absolute",
        top: 16,
        right: 20,
        border: "solid 1px #E6E6E6",
    },
    numberInput: {
        color: "black",
        textAlign: "right",
        fontSize: 14,
        height: 19,
    },
    numberHint: {
        textAlign: "center",
    },
    attributeSelect: {
        color: "black",
        width: "100%",
        fontSize: 14,
    },
    right: {
        position: "absolute",
        right: 10,
    },
    subSectionLabel: {
        marginTop: 15,
        marginBottom: 7,
        fontSize: 14,
    },
    numberFormat: {
        width: 81,
        position: "absolute",
        right: 10,
        color: "#434343",
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
    },
}));

type globalState = {
    showCollars: boolean;
    showCollarLabels: boolean;
    showCylinders: boolean;
    showAttributes: boolean;

    attributes: string;
    attributesTextSize: number;
    collarColour: string;
    collarSize: number;
    collarTextSize: number;
    scaleMode: number;

    //radius
    radiusAttributes: string;
    radiusAttributeIndex: number;
    radiusAttributeRangeMin: number;
    radiusAttributeRangeMax: number;
    radiusAttributeRangeStep: number;
    radiusRangeMin: number;
    radiusRangeMax: number;
    cylinderRadius: number;

    //geo
    geoShowZ: boolean;
    geoShowLabels: boolean;
    geoLabelSize: number;
    geoShowElevation: boolean;
    geoPlaneValue: number;
    geoAttributes: {
        top: {
            attribute: string;
            color: string;
            index: number;
        };
        bottom: {
            attribute: string;
            color: string;
            index: number;
        };
        left: {
            attribute: string;
            color: string;
            index: number;
        };
        right: {
            attribute: string;
            color: string;
            index: number;
        };
    };
    geoSize: number;
    geoShape: number;
};

let globalSettings: globalState[] = [];

const { isLimboState, getDrillHoleSettings, getAttributes, getAttributeIndex, getSourceEntity } = GlobalActions;

const getAttributeIndices = function (layer: any, radiusAttributeListIndex: number, radiusAttributes: string) {
    let attributeIndices = [];

    const elements: string[] = radiusAttributes.split(" ");
    const elementCount: number = elements.length;

    for (let elementIndex: number = 0; elementIndex < elementCount; ++elementIndex) {
        const element: string = elements[elementIndex];
        if (element !== "") {
            const subElements: string[] = element.split(",");
            const attributeName: string = subElements[0];
            const attributeIndex: number = getAttributeIndex(attributeName, layer);
            const attributeListIndex: number = parseInt(subElements[1]);
            if (attributeListIndex === radiusAttributeListIndex) {
                attributeIndices.push(attributeIndex);
            }
        }
    }
    return attributeIndices;
};

function updateDrillholeSettings(layer: any) {
    globalSettings[layer.ID] = getDrillHoleSettings(layer);
    const { showCollars, showCollarLabels, showCylinders, showAttributes, attributes, attributesTextSize } =
        globalSettings[layer.ID];

    const leftAttributeIndices: number[] = getAttributeIndices(layer, 0, attributes);
    const rightAttributeIndices: number[] = getAttributeIndices(layer, 1, attributes);

    var drillholeSettingData = {
        showCollars: showCollars,
        showCollarLabels: showCollarLabels,
        showCylinders: showCylinders,
        showAttributes: showAttributes,
        attributesTextSize: attributesTextSize,
        attributes: attributes,
        leftAttributeIndices: leftAttributeIndices,
        rightAttributeIndices: rightAttributeIndices,
    };
    handleLayerEvent("setDrillholeSettings", drillholeSettingData, layer);
}

function updateGeoChemSettings(layer: any) {
    globalSettings[layer.ID] = getDrillHoleSettings(layer);
    const { geoShowZ, geoShowLabels, geoLabelSize, geoShowElevation, geoPlaneValue, geoAttributes, geoSize, geoShape } =
        globalSettings[layer.ID];

    const geoSettingData = {
        showPolygon: false, //geoShowPolygon,
        showInside: true, //!geoShowPolygonOutside,
        polygonPoints: { vfPoints: [], wPoints: [] }, //polygonPoints,
        showZ: geoShowZ,
        geoShowLabels: geoShowLabels,
        geoLabelSize: geoLabelSize,
        geoShowElevation: geoShowElevation,
        planeValue: geoPlaneValue,
        attributes: geoAttributes,
        geoSize: geoSize,
        geoShape: geoShape,
    };
    console.log("geoSettingData:", geoSettingData);
    handleLayerEvent("setGeoChemSettings", geoSettingData, layer);
}

type IDrillHoleSettings = {
    layer: any;
    selectedLayers: any;
    currentProject: any;
    attributeQueryEntity: string;
};

function DrillHoleFilter(props: IDrillHoleSettings) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    var { layer, selectedLayers } = props;
    const [needsApply, setNeedsApply] = useState(false);
    const [labelFilter, setLabelFilter] = useState("");

    useEffect(() => {
        if (typeof layer["label_query_builder"] !== "undefined") {
            setLabelFilter(layer["label_query_builder"]);
        } else {
            setLabelFilter("");
        }
    }, [layer]);

    var isMultiple = false;
    if (selectedLayers.length === 1) {
        layer = selectedLayers[0];
    } else {
        layer = props.layer;
        isMultiple = true;
        if (isLimboState(selectedLayers, "label_query_builder") && labelFilter === "Multi") {
            setLabelFilter("Multi");
        }
    }

    const handleChange = (e: any) => {
        setLabelFilter(e.target.value);
        setNeedsApply(true);
    };

    function applySettings() {
        setNeedsApply(false);
        var data = {
            builderLabelFilters: labelFilter.split(" "),
            builderLabelRawData: labelFilter,
        };
        handleLayerEvent("setLabelQueryBuilder", data, layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                handleLayerEvent("setLabelQueryBuilder", data, currentLayer);
            });
        }
    }

    return (
        <Section name="drillHoleFilter" label="Filter">
            <div className={common.sectionContent}>
                <div
                    className={classes.row}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        marginLeft: -4,
                    }}
                >
                    <Typography
                        className={classes.label}
                        style={{
                            display: "inline-flex",
                            paddingLeft: 1,
                            paddingRight: 25,
                        }}
                    >
                        By Collar ID
                    </Typography>
                    <TextField
                        sx={{
                            '& .MuiInputBase-input': {
                                padding: "5px 14px",
                            }
                        }}
                        style={{ width: 156, position: "absolute", right: 10 }}
                        onChange={handleChange}
                        value={
                            isMultiple && isLimboState(selectedLayers, "label_query_builder") ? "Multi" : labelFilter
                        }
                    />
                </div>
            </div>
            {needsApply ? (
                <div className={classes.bottomButtonContainer}>
                    <Button
                        onClick={applySettings}
                        title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                    >
                        Apply
                    </Button>
                </div>
            ) : (
                ""
            )}
        </Section>
    );
}

function DrillHoleCylinders(props: IDrillHoleSettings) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const [update, setUpdate] = useState(false);
    const [needsApply, setNeedsApply] = useState(false);
    var { layer, selectedLayers } = props;
    var isMultiple = false;
    if (selectedLayers.length === 1) {
        layer = selectedLayers[0];
    } else {
        layer = props.layer;
        isMultiple = true;
    }

    const {
        showCylinders,
        scaleMode,
        radiusAttributes,
        cylinderRadius,
        radiusAttributeRangeMin,
        radiusAttributeRangeMax,
        radiusAttributeRangeStep,
        radiusRangeMin,
        radiusRangeMax,
    } = globalSettings[layer.ID];

    function applySettings() {
        const {
            scaleMode,
            radiusAttributes,
            radiusAttributeIndex,
            cylinderRadius,
            radiusAttributeRangeMin,
            radiusAttributeRangeMax,
            radiusAttributeRangeStep,
            radiusRangeMin,
            radiusRangeMax,
        } = globalSettings[layer.ID];

        var tubeDiameterData = {
            scaleMode: scaleMode,
            cylinderRadius: cylinderRadius,
            attribute: radiusAttributes,
            attributeIndex: radiusAttributeIndex,
            rangeMin: radiusAttributeRangeMin,
            rangeMax: radiusAttributeRangeMax,
            rangeStep: radiusAttributeRangeStep,
            radiusMin: radiusRangeMin,
            radiusMax: radiusRangeMax,
        };
        handleLayerEvent("setTubeDiameterLegend", tubeDiameterData, layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                handleLayerEvent("setTubeDiameterLegend", tubeDiameterData, currentLayer);
            });
        }
        setNeedsApply(false);
    }

    function setDrillHoleSettingsShowCylinders(value: boolean) {
        globalSettings[layer.ID]["showCylinders"] = value;
        layer["drillholes_show_cylinders"] = value ? "1" : "0";
        updateDrillholeSettings(layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["showCylinders"] = value;
                currentLayer["drillholes_show_cylinders"] = value ? "1" : "0";
                updateDrillholeSettings(currentLayer);
            });
        }
        setUpdate(!update);
    }

    function setDrillHoleSettingsScaleMode(value: boolean) {
        globalSettings[layer.ID]["scaleMode"] = value ? 2 : 1;
        layer["tube_legend_scale_mode"] = value ? "2" : "1";
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["scaleMode"] = value ? 2 : 1;
                currentLayer["tube_legend_scale_mode"] = value ? "2" : "1";
            });
        }
        applySettings();
        setUpdate(!update);
    }

    function setAttribute(name: string, value: any) {
        function switchAttribute(layer: any, name: string, value: any) {
            switch (name) {
                case "radiusAttributes":
                    globalSettings[layer.ID]["radiusAttributes"] = value;
                    var index: number = findIndex(value);
                    globalSettings[layer.ID]["radiusAttributeIndex"] = index;
                    layer["tube_legend_attribute"] = value;
                    layer["tube_legend_attribute_index"] = index;
                    break;
                case "radiusAttributeRangeMin":
                    globalSettings[layer.ID]["radiusAttributeRangeMin"] = value;
                    layer["tube_legend_range_min"] = value;
                    break;
                case "radiusAttributeRangeMax":
                    globalSettings[layer.ID]["radiusAttributeRangeMax"] = value;
                    layer["tube_legend_range_max"] = value;
                    break;
                case "radiusAttributeRangeStep":
                    globalSettings[layer.ID]["radiusAttributeRangeStep"] = value;
                    layer["tube_legend_range_step"] = value;
                    break;
                case "radiusRangeMin":
                    globalSettings[layer.ID]["radiusRangeMin"] = value;
                    layer["tube_legend_radius_min"] = value;
                    break;
                case "radiusRangeMax":
                    globalSettings[layer.ID]["radiusRangeMax"] = value;
                    layer["tube_legend_radius_max"] = value;
                    break;
                case "cylinderRadius":
                    globalSettings[layer.ID]["cylinderRadius"] = value;
                    layer["tube_legend_radius"] = value;
                    break;
            }
        }
        switchAttribute(layer, name, value);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                switchAttribute(currentLayer, name, value);
            });
        }
        setUpdate((prevVal) => !prevVal);
        setNeedsApply(true);
    }

    let attributes = getAttributes(layer.ID);
    attributes = attributes.filter((attribute: string) => attribute !== "voxelfarm_attribute");

    function findIndex(value: any) {
        return attributes.findIndex((elm: any) => elm === value);
    }

    const setLimboValue = (name: string, value: any) => {
        if (name === "tube_legend_scale_mode") {
            layer[name] = value ? "2" : "1";
        } else {
            layer[name] = value ? "1" : "0";
        }
        globalSettings[layer.ID]["scaleMode"] = value ? 2 : 1;

        selectedLayers.forEach((currentLayer: any) => {
            if (name === "tube_legend_scale_mode") {
                currentLayer[name] = value ? "2" : "1";
            } else {
                currentLayer[name] = value ? "1" : "0";
            }
            globalSettings[currentLayer.ID]["scaleMode"] = value ? 2 : 1;
        });
        setUpdate(!update);
    };

    return (
        <Section name="drillHoleCylinders" label="Segments">
            <div className={common.sectionContent}>
                <table style={{ marginLeft: -6, width: "100%" }} cellPadding={0} cellSpacing={0}>
                    <tbody>
                        <tr className={classes.row}>
                            <td style={{ width: "100%" }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Show Cylinders
                                </Typography>
                            </td>
                            <td>
                                <div style={{ position: "relative", left: -3 }}>
                                    {isMultiple && isLimboState(selectedLayers, "drillholes_show_cylinders") ? (
                                        <Switcher
                                            onClick={(evt: any) => {
                                                setLimboValue("drillholes_show_cylinders", true);
                                            }}
                                        />
                                    ) : (
                                        <Switch
                                            size="small"
                                            checked={showCylinders}
                                            onChange={(evt: any) => {
                                                setDrillHoleSettingsShowCylinders(evt.target.checked);
                                            }}
                                        />
                                    )}
                                </div>
                            </td>
                        </tr>
                        <tr className={classes.row}>
                            <td style={{ width: "100%" }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Scale Cylinder
                                </Typography>
                            </td>
                            <td>
                                <div style={{ position: "relative", left: -3 }}>
                                    {isMultiple && isLimboState(selectedLayers, "tube_legend_scale_mode") ? (
                                        <Switcher
                                            onClick={(evt: any) => {
                                                setLimboValue("tube_legend_scale_mode", true);
                                            }}
                                        />
                                    ) : (
                                        <Switch
                                            size="small"
                                            checked={scaleMode === 2}
                                            onChange={(evt: any) => {
                                                setDrillHoleSettingsScaleMode(evt.target.checked);
                                            }}
                                        />
                                    )}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2} style={{ width: "100%" }}>
                                <div style={{ display: scaleMode === 2 ? "block" : "none" }}>
                                    <div className={classes.row}>
                                        <Typography
                                            className={classes.label}
                                            style={{
                                                display: "inline-flex",
                                                paddingLeft: 1,
                                                paddingTop: 4,
                                            }}
                                        >
                                            Radius Attribute
                                        </Typography>
                                        <Select
                                            value={
                                                radiusAttributes.length > 0 && radiusAttributes !== ""
                                                    ? isMultiple &&
                                                      isLimboState(selectedLayers, "tube_legend_attribute")
                                                        ? "Multi"
                                                        : radiusAttributes
                                                    : "Default"
                                            }
                                            className={classes.attributeSelect}
                                            style={{ width: 130, height: 30, position: "absolute", right: 10 }}
                                            onChange={(evt: any) => {
                                                setAttribute("radiusAttributes", evt.target.value);
                                            }}
                                        >
                                            <MenuItem value="Default" style={{ display: "none" }} disabled>
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="Multi" style={{ display: "none" }}>
                                                Multi Values
                                            </MenuItem>
                                            {attributes.map((attributeId: any) => {
                                                return (
                                                    <MenuItem key={attributeId} value={attributeId}>
                                                        {attributeId}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </div>
                                    <div className={classes.row}>
                                        <Typography
                                            className={classes.label}
                                            style={{
                                                display: "inline-flex",
                                                paddingLeft: 1,
                                                paddingTop: 4,
                                            }}
                                        >
                                            Data Step
                                        </Typography>
                                        <DashInput
                                            name={layer.ID + "-radiusAttributeRangeStep"}
                                            value={radiusAttributeRangeStep}
                                            className={[classes.numberInput, classes.numberFormat].join(" ")}
                                            inputProps={{ min: 1 }}
                                            style={{ width: 14, height: 10, left: 131, top: 4 }}
                                            onChange={(evt: any) => {
                                                evt.stopPropagation();
                                                setAttribute("radiusAttributeRangeStep", parseFloat(evt.target.value));
                                            }}
                                            isLimbo={
                                                isMultiple && isLimboState(selectedLayers, "tube_legend_range_step")
                                            }
                                        />
                                    </div>
                                    <div className={classes.row}>
                                        <Typography
                                            className={classes.label}
                                            style={{
                                                display: "inline-flex",
                                                paddingLeft: 1,
                                                paddingRight: 85,
                                            }}
                                        >
                                            Data Range
                                        </Typography>
                                        <Grid
                                            container
                                            direction="row"
                                            xs={6}
                                            item={true}
                                            style={{ position: "absolute", right: 10 }}
                                        >
                                            <Grid xs={5} item={true}>
                                                <DashInput
                                                    name={layer.ID + "-radiusAttributeRangeMin"}
                                                    value={radiusAttributeRangeMin}
                                                    fullWidth={true}
                                                    className={classes.numberInput}
                                                    inputProps={{ style: { textAlign: "right" } }}
                                                    style={{ width: 14, height: 10, left: 30, top: 4 }}
                                                    onChange={(evt: any) => {
                                                        console.log("target", evt.target);
                                                        evt.preventDefault();
                                                        evt.stopPropagation();
                                                        setAttribute(
                                                            "radiusAttributeRangeMin",
                                                            parseFloat(evt.target.value)
                                                        );
                                                    }}
                                                    isLimbo={
                                                        isMultiple &&
                                                        isLimboState(selectedLayers, "tube_legend_range_min")
                                                    }
                                                />
                                            </Grid>
                                            <Grid xs={7} item={true}>
                                                <DashInput
                                                    name={layer.ID + "-radiusAttributeRangeMax"}
                                                    value={radiusAttributeRangeMax}
                                                    fullWidth={true}
                                                    className={classes.numberInput}
                                                    inputProps={{ style: { textAlign: "right" } }}
                                                    style={{ width: 23, height: 10, left: 45, top: 4 }}
                                                    onChange={(evt: any) => {
                                                        setAttribute(
                                                            "radiusAttributeRangeMax",
                                                            parseFloat(evt.target.value)
                                                        );
                                                    }}
                                                    isLimbo={
                                                        isMultiple &&
                                                        isLimboState(selectedLayers, "tube_legend_range_max")
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>
                                    <div className={classes.row}>
                                        <Typography
                                            className={classes.label}
                                            style={{
                                                display: "inline-flex",
                                                paddingLeft: 1,
                                                paddingRight: 85,
                                            }}
                                        >
                                            Radius Range
                                        </Typography>
                                        <Grid
                                            container
                                            direction="row"
                                            xs={6}
                                            item={true}
                                            style={{ position: "absolute", right: 10 }}
                                        >
                                            <Grid xs={5} item={true}>
                                                <DashInput
                                                    name={layer.ID + "-radiusRangeMin"}
                                                    value={radiusRangeMin}
                                                    fullWidth={true}
                                                    className={classes.numberInput}
                                                    inputProps={{
                                                        style: { textAlign: "right" },
                                                        min: 0,
                                                    }}
                                                    style={{ width: 14, height: 10, left: 30, top: 4 }}
                                                    onChange={(evt: any) => {
                                                        setAttribute("radiusRangeMin", parseFloat(evt.target.value));
                                                    }}
                                                    isLimbo={
                                                        isMultiple &&
                                                        isLimboState(selectedLayers, "tube_legend_radius_min")
                                                    }
                                                />{" "}
                                            </Grid>
                                            <Grid xs={7} item={true}>
                                                <DashInput
                                                    name={layer.ID + "-radiusRangeMax"}
                                                    value={radiusRangeMax}
                                                    fullWidth={true}
                                                    className={classes.numberInput}
                                                    inputProps={{
                                                        style: { textAlign: "right" },
                                                        min: 1,
                                                    }}
                                                    style={{ width: 23, height: 10, left: 45, top: 4 }}
                                                    onChange={(evt: any) => {
                                                        setAttribute("radiusRangeMax", parseFloat(evt.target.value));
                                                    }}
                                                    isLimbo={
                                                        isMultiple &&
                                                        isLimboState(selectedLayers, "tube_legend_radius_max")
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>
                                </div>
                                <div
                                    className={classes.row}
                                    style={{
                                        alignItems: "center",
                                        display: scaleMode === 1 ? "inline-flex" : "none",
                                    }}
                                >
                                    <Typography
                                        className={classes.label}
                                        style={{
                                            display: "inline-flex",
                                            flexGrow: 1,
                                            paddingLeft: 1,
                                            paddingTop: 4,
                                        }}
                                    >
                                        Cylinder Radius
                                    </Typography>
                                    <DashInput
                                        name={layer.ID + "-cylinderRadius"}
                                        value={cylinderRadius}
                                        style={{ width: 76, height: 10, left: -91, top: 4 }}
                                        onChange={(evt: any) => {
                                            setAttribute("cylinderRadius", parseFloat(evt.target.value));
                                        }}
                                        type="multivalues"
                                        inputProps={{ min: 1 }}
                                        className={[classes.numberInput, classes.numberFormat].join(" ")}
                                        isLimbo={isMultiple && isLimboState(selectedLayers, "tube_legend_radius")}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {needsApply ? (
                <div className={classes.bottomButtonContainer}>
                    <Button
                        onClick={applySettings}
                        title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                    >
                        Apply
                    </Button>
                </div>
            ) : (
                ""
            )}
        </Section>
    );
}

function DrillHoleCollars(props: IDrillHoleSettings) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const [update, setUpdate] = useState(false);
    const [needsApply, setNeedsApply] = useState(false);
    var { layer, selectedLayers } = props;

    var isMultiple = false;
    if (selectedLayers.length === 1) {
        layer = selectedLayers[0];
    } else {
        layer = props.layer;
        isMultiple = true;
    }

    if (isMultiple) {
        var isLimboShowCollars = isLimboState(selectedLayers, "drillholes_show_collars");
        if (!isLimboShowCollars) {
            globalSettings[layer.ID].showCollars = selectedLayers[0]["drillholes_show_collars"] === "1";
        }
        var isLimboShowCollarsLabels = isLimboState(selectedLayers, "drillholes_show_collars_labels");
        if (!isLimboShowCollarsLabels) {
            globalSettings[layer.ID].showCollarLabels = selectedLayers[0]["drillholes_show_collar_labels"] === "1";
        }
    }

    const { showCollars, showCollarLabels, collarColour, collarSize, collarTextSize } = globalSettings[layer.ID];
    let drillholesColorRef: any;

    function applySettings() {
        const { collarColour, collarSize, collarTextSize } = globalSettings[layer.ID];

        const collarData = {
            collarMode: "1",
            collarColour: collarColour,
            collarSize: collarSize,
            collarTextSize: collarTextSize,
        };

        handleLayerEvent("setCollarLegend", collarData, layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                handleLayerEvent("setCollarLegend", collarData, currentLayer);
            });
        }

        setNeedsApply(false);
    }

    function setDrillHoleSettingsShowCollars(value: boolean) {
        globalSettings[layer.ID]["showCollars"] = value;
        layer["drillholes_show_collars"] = value ? "1" : "0";
        updateDrillholeSettings(layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["showCollars"] = value;
                currentLayer["drillholes_show_collars"] = value ? "1" : "0";
                updateDrillholeSettings(layer);
            });
        }
        setUpdate(!update);
    }

    function setDrillHoleSettingsShowCollarLabels(value: boolean) {
        globalSettings[layer.ID]["showCollarLabels"] = value;
        layer["drillholes_show_collar_labels"] = value ? "1" : "0";
        updateDrillholeSettings(layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["showCollarLabels"] = value;
                currentLayer["drillholes_show_collar_labels"] = value ? "1" : "0";
                updateDrillholeSettings(layer);
            });
        }
        setUpdate(!update);
    }

    const showPalette = (e: any) => {
        if (drillholesColorRef.style.display === "block") {
            drillholesColorRef.style.display = "none";
        } else {
            drillholesColorRef.style.display = "block";
        }
    };

    const handleColorChange = (newValue: any) => {
        globalSettings[layer.ID]["collarColour"] = newValue.hex.toUpperCase();
        layer["collar_legend_color"] = newValue.hex.toUpperCase();
        drillholesColorRef.style.display = "none";

        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["collarColour"] = newValue.hex.toUpperCase();
                currentLayer["collar_legend_color"] = newValue.hex.toUpperCase();
            });
        }
        //updateDrillholeSettings(layer);
        setUpdate(!update);
        setNeedsApply(true);
    };

    const handleCollarSize = (e: any) => {
        const newValue = parseInt(e.target.value);
        globalSettings[layer.ID]["collarSize"] = newValue;
        layer["collar_legend_radius"] = newValue;

        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["collarSize"] = newValue;
                currentLayer["collar_legend_radius"] = newValue;
            });
        }
        //updateDrillholeSettings(layer);
        setUpdate(!update);
        setNeedsApply(true);
    };

    const handleCollarTextSize = (e: any) => {
        const newValue = parseFloat(e.target.value);
        globalSettings[layer.ID]["collarTextSize"] = newValue;
        layer["collar_legend_text_size"] = newValue;

        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["collarTextSize"] = newValue;
                currentLayer["collar_legend_text_size"] = newValue;
            });
        }
        //updateDrillholeSettings(layer);
        setUpdate(!update);
        setNeedsApply(true);
    };

    const setLimboValue = (name: string, value: any) => {
        layer[name] = value ? "1" : "0";
        selectedLayers.forEach((currentLayer: any) => {
            currentLayer[name] = value ? "1" : "0";
        });
        setUpdate(!update);
    };

    return (
        <Section name="drillHoleCollars" label="Collars">
            <div className={common.sectionContent}>
                <table style={{ marginLeft: -6 }} cellPadding="0" cellSpacing="0">
                    <tbody>
                        <tr className={classes.row}>
                            <td style={{ width: 239 }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Show Collars
                                </Typography>
                            </td>
                            <td>
                                {isMultiple && isLimboState(selectedLayers, "drillholes_show_collars") ? (
                                    <Switcher
                                        onClick={(evt: any) => {
                                            setLimboValue("drillholes_show_collars", true);
                                        }}
                                    />
                                ) : (
                                    <Switch
                                        size="small"
                                        checked={showCollars}
                                        onChange={(evt: any) => {
                                            setDrillHoleSettingsShowCollars(evt.target.checked);
                                        }}
                                    />
                                )}
                            </td>
                        </tr>
                        <tr className={classes.row}>
                            <td style={{ width: "100%" }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Show Collars Label
                                </Typography>
                            </td>
                            <td>
                                {isMultiple && isLimboState(selectedLayers, "drillholes_show_collar_labels") ? (
                                    <Switcher
                                        onClick={(evt: any) => {
                                            setLimboValue("drillholes_show_collars_labels", true);
                                        }}
                                    />
                                ) : (
                                    <Switch
                                        size="small"
                                        checked={showCollarLabels}
                                        onChange={(evt: any) => {
                                            setDrillHoleSettingsShowCollarLabels(evt.target.checked);
                                        }}
                                    />
                                )}
                            </td>
                        </tr>
                        <tr className={classes.row}>
                            <td style={{ width: "100%" }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Collar Colour
                                </Typography>
                            </td>
                            <td>
                                <div style={{ position: "relative", paddingLeft: 5 }}>
                                    <input
                                        value={collarColour}
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            handleColorChange(e.target.value);
                                        }}
                                    />
                                    <div
                                        className={classes.colorBox}
                                        style={{ backgroundColor: collarColour, marginTop: -12 }}
                                        onClick={showPalette}
                                    ></div>
                                    <ArrowDropDown
                                        className={classes.arrow}
                                        style={{ position: "relative", top: 3, left: -3 }}
                                        onClick={showPalette}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td ref={(elm) => (drillholesColorRef = elm)} colSpan={2} style={{ display: "none" }}>
                                <div className={classes.palette}>
                                    <SketchPicker color={collarColour} onChangeComplete={handleColorChange} />
                                </div>
                            </td>
                        </tr>
                        <tr className={classes.row}>
                            <td colSpan={2} style={{ width: "100%", display: "inline-flex" }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Collar Size
                                </Typography>
                                <DashInput
                                    name={layer.ID + "-collarSize"}
                                    value={collarSize}
                                    style={{ width: 76, height: 10, left: 118, top: 4 }}
                                    onChange={handleCollarSize}
                                    type="multivalues"
                                    inputProps={{ min: 1 }}
                                    className={[classes.numberInput, classes.numberFormat].join(" ")}
                                    isLimbo={isMultiple && isLimboState(selectedLayers, "collar_legend_radius")}
                                />
                            </td>
                        </tr>
                        <tr className={classes.row}>
                            <td colSpan={2} style={{ width: "100%", display: "inline-flex" }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Collar Text Size
                                </Typography>
                                <DashInput
                                    name={layer.ID + "-collarTextSize"}
                                    value={collarTextSize}
                                    style={{ width: 76, height: 10, left: 118, top: 4 }}
                                    onChange={handleCollarTextSize}
                                    type="multivalues"
                                    inputProps={{ min: 0.1, step: 0.1 }}
                                    className={[classes.numberInput, classes.numberFormat].join(" ")}
                                    isLimbo={isMultiple && isLimboState(selectedLayers, "collar_legend_text_size")}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {needsApply ? (
                <div className={classes.bottomButtonContainer}>
                    <Button
                        onClick={applySettings}
                        title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                    >
                        Apply
                    </Button>
                </div>
            ) : (
                ""
            )}
        </Section>
    );
}

function GeoChemCollars(props: IDrillHoleSettings) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const [update, setUpdate] = useState(false);
    const [needsApply, setNeedsApply] = useState(false);
    var { layer, selectedLayers } = props;

    var isMultiple = false;
    if (selectedLayers.length === 1) {
        layer = selectedLayers[0];
    } else {
        layer = props.layer;
        isMultiple = true;
    }

    const { geoSize } = globalSettings[layer.ID];

    const handlegeoSize = (e: any) => {
        const newValue = parseInt(e.target.value);
        globalSettings[layer.ID]["geoSize"] = newValue;
        layer["geo_chem_size"] = newValue;

        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["geoSize"] = newValue;
                currentLayer["geo_chem_size"] = newValue;
            });
        }
        //updateGeoChemSettings(layer);
        setUpdate(!update);
        setNeedsApply(true);
    };

    function applySettings() {
        updateGeoChemSettings(layer);
        setNeedsApply(false);
    }

    return (
        <Section name="geoCollars" label="Collars">
            <div className={common.sectionContent}>
                <table style={{ marginLeft: -6 }} cellPadding="0" cellSpacing="0">
                    <tbody>
                        <tr className={classes.row}>
                            <td style={{ width: 239 }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Show Collars Label
                                </Typography>
                            </td>
                        </tr>
                        <tr className={classes.row}>
                            <td colSpan={2} style={{ width: "100%", display: "inline-flex" }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Collar Size
                                </Typography>
                                <DashInput
                                    name={layer.ID + "-geoSize"}
                                    value={geoSize}
                                    style={{ width: 76, height: 10, left: 118, top: 4 }}
                                    onChange={handlegeoSize}
                                    type="multivalues"
                                    inputProps={{ min: 1 }}
                                    className={[classes.numberInput, classes.numberFormat].join(" ")}
                                    isLimbo={isMultiple && isLimboState(selectedLayers, "geo_chem_size")}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {needsApply ? (
                <div className={classes.bottomButtonContainer}>
                    <Button
                        onClick={applySettings}
                        title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                    >
                        Apply
                    </Button>
                </div>
            ) : (
                ""
            )}
        </Section>
    );
}

function DrillHoleAttributes(props: IDrillHoleSettings) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const [update, setUpdate] = useState(false);
    const [needsApply, setNeedsApply] = useState(false);
    var { layer, selectedLayers } = props;
    var isMultiple = false;
    if (selectedLayers.length === 1) {
        layer = selectedLayers[0];
    } else {
        layer = props.layer;
        isMultiple = true;
    }

    const { showAttributes, attributes } = globalSettings[layer.ID];
    const MaximumVisibleAttributes: number = 3;

    function setDrillHoleSettingsShowAttributes(value: boolean) {
        globalSettings[layer.ID]["showAttributes"] = value;
        layer["drillholes_show_attributes"] = value ? "1" : "0";
        updateDrillholeSettings(layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["showAttributes"] = value;
                currentLayer["drillholes_show_attributes"] = value ? "1" : "0";
                updateDrillholeSettings(currentLayer);
            });
        }
        setUpdate(!update);
    }

    function setAttributes(value: string) {
        globalSettings[layer.ID]["attributes"] = value;
        layer["drillholes_attributes"] = value;
        updateDrillholeSettings(layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["attributes"] = value;
                currentLayer["drillholes_attributes"] = value ? "1" : "0";
                updateDrillholeSettings(currentLayer);
            });
        }
        setUpdate((prevVal) => !prevVal);
    }

    function getAttributeList(list: number) {
        let entries: any = attributes.split(" ");
        let result: any = [];
        entries.forEach((entry: string, index: number) => {
            let items = entry.split(",");
            if (parseInt(items[1]) === list) result.push(items[0]);
        });
        return result.splice(0, MaximumVisibleAttributes);
    }

    function removeAttribute(list: number, index: number) {
        let lists: any = [getAttributeList(0), getAttributeList(1)];
        lists[list].splice(index, 1);
        setAttributesFromArrays(lists);
    }

    function setAttributesFromArrays(lists: any) {
        let result: string = "";
        let count: number = 0;
        lists[0].forEach((item: string, index: number) => {
            if (count++ > 0) result += " ";
            result += item + ",0";
        });
        lists[1].forEach((item: string, index: number) => {
            if (count++ > 0) result += " ";
            result += item + ",1";
        });
        setAttributes(result);
        //setNeedsApply(true);
    }

    function setAttribute(list: number, index: number, value: string) {
        let lists: any = [getAttributeList(0), getAttributeList(1)];
        lists[list][index] = value;
        setAttributesFromArrays(lists);
    }

    function addAttributeRow(list: number, attribute: any) {
        let newDrillholeSettingsAttributes = "";
        if (attributes !== "") {
            newDrillholeSettingsAttributes = attributes + " " + attribute + "," + list;
        } else {
            newDrillholeSettingsAttributes = attribute + "," + list;
        }
        setAttributes(newDrillholeSettingsAttributes);
        //setNeedsApply(true);
    }

    function getAttributeEntries(list: number, hint: string) {
        let attributes: any = getAttributes(layer.ID);
        attributes = attributes.filter((attribute: string) => attribute !== "voxelfarm_attribute");
        let attributeList: any = getAttributeList(list);

        return (
            <Fragment>
                {attributeList.map((item: string, index: number) => {
                    return (
                        <Select
                            key={item}
                            value={isMultiple && isLimboState(selectedLayers, "drillholes_attributes") ? "Multi" : item}
                            className={classes.attributeSelect}
                            onChange={(evt: any) => {}}
                            sx={{
                                '& .MuiInputBase-input': {
                                    padding: "5px 14px",
                                }
                            }}
                        >
                            <MenuItem
                                onClick={(evt: any) => {
                                    removeAttribute(list, index);
                                }}
                            >
                                <HighlightOff style={{ marginRight: 10 }} />
                                Remove
                            </MenuItem>
                            <MenuItem value="all" style={{ display: "none" }} disabled>
                                <em></em>
                            </MenuItem>
                            <MenuItem value="Multi" style={{ display: "none" }} disabled>
                                Multi Values
                            </MenuItem>
                            {attributes.map((attributeId: any) => {
                                return (
                                    <MenuItem
                                        key={attributeId}
                                        value={attributeId}
                                        onClick={(evt: any) => {
                                            setAttribute(list, index, attributeId);
                                        }}
                                    >
                                        {attributeId}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    );
                })}
                {attributeList.length < MaximumVisibleAttributes ? (
                    <Select
                        value={isMultiple && isLimboState(selectedLayers, "drillholes_attributes") ? "Multi" : "None"}
                        className={classes.attributeSelect}
                        onChange={(evt: any) => {
                            addAttributeRow(list, evt.target.value);
                        }}
                        style={{ color: "silver", fontStyle: "italic" }}
                        sx={{
                            '& .MuiInputBase-input': {
                                padding: "5px 14px",
                            }
                        }}
                    >
                        <MenuItem value="None">None</MenuItem>
                        <MenuItem value="all" style={{ display: "none" }} disabled>
                            <em></em>
                        </MenuItem>
                        <MenuItem value="Multi" style={{ display: "none" }} disabled>
                            Multi Values
                        </MenuItem>
                        {attributes.map((attributeId: any) => {
                            return (
                                <MenuItem key={attributeId} value={attributeId}>
                                    {attributeId}
                                </MenuItem>
                            );
                        })}
                    </Select>
                ) : (
                    ""
                )}
                <FormHelperText>{hint}</FormHelperText>
            </Fragment>
        );
    }

    const setLimboValue = (name: string, value: any) => {
        layer[name] = value ? "1" : "0";
        selectedLayers.forEach((currentLayer: any) => {
            currentLayer[name] = value ? "1" : "0";
        });
        setUpdate(!update);
    };

    function applySettings() {
        setNeedsApply(false);
    }

    return (
        <Section name="drillHoleAttributes" label="Attribute Values">
            <div className={common.sectionContent}>
                <table style={{ marginLeft: -6, width: "100%" }} cellPadding={0} cellSpacing={0}>
                    <tbody>
                        <tr className={classes.row}>
                            <td style={{ width: "100%" }}>
                                <Typography
                                    className={classes.label}
                                    style={{ display: "inline-flex", paddingLeft: 1 }}
                                >
                                    Show Attributes
                                </Typography>
                            </td>
                            <td>
                                <div style={{ position: "relative", left: -3 }}>
                                    {isMultiple && isLimboState(selectedLayers, "drillholes_show_attributes") ? (
                                        <Switcher
                                            onClick={(evt: any) => {
                                                setLimboValue("drillholes_show_attributes", true);
                                            }}
                                        />
                                    ) : (
                                        <Switch
                                            size="small"
                                            checked={showAttributes}
                                            onChange={(evt: any) => {
                                                setDrillHoleSettingsShowAttributes(evt.target.checked);
                                            }}
                                        />
                                    )}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2}>
                                <Fragment>
                                    <Typography className={classes.subSectionLabel}>
                                        Select Attributes to display
                                    </Typography>
                                    <Grid container direction="row" style={{ paddingBottom: 9 }}>
                                        <Grid item xs={6} style={{ paddingRight: 5 }}>
                                            {getAttributeEntries(0, "Left-side Attributes")}
                                        </Grid>
                                        <Grid item xs={6} style={{ paddingLeft: 5 }}>
                                            {getAttributeEntries(1, "Right-side Attributes")}
                                        </Grid>
                                    </Grid>
                                </Fragment>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {needsApply ? (
                <div className={classes.bottomButtonContainer}>
                    <Button
                        onClick={applySettings}
                        title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                    >
                        Apply
                    </Button>
                </div>
            ) : (
                ""
            )}
        </Section>
    );
}

interface IAttributeList extends IDrillHoleSettings {
    position: string;
}

function AttributeList(props: IAttributeList) {
    const { classes } = useStyles();
    var { layer, position } = props;
    const [update, setUpdate] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const { getAttributesObjects } = GlobalActions;
    const attributes: any = getAttributesObjects(layer.ID);
    const geoAttributes: any = globalSettings[layer.ID]["geoAttributes"];
    const colorAttribute: any = geoAttributes[position];

    const setAttribute = (attribute: any) => {
        colorAttribute["attribute"] = attribute.name;
        colorAttribute["index"] = attribute.index;
        layer["geo_chem_" + position + "_attribute"] = attribute.name;
        layer["geo_chem_" + position + "_index"] = attribute.index;
        updateGeoChemSettings(layer);
        setUpdate((prevUpdate) => !prevUpdate);
    };
    const handleColorChange = (newValue: any) => {
        colorAttribute["color"] = newValue.hex;
        layer["geo_chem_" + position + "_color"] = newValue.hex;
        setUpdate((prevUpdate) => !prevUpdate);
        updateGeoChemSettings(layer);
    };
    const getLabel = (colorAttribute: any) => {
        let label = colorAttribute["name"] || colorAttribute;
        attributes.forEach((attribute: any) => {
            if (attribute.name === label) {
                label = attribute.label;
            }
        });
        return label;
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    return (
        <Grid item xs={5} style={{ userSelect: update ? "none" : "none" }}>
            <CustomSelect
                inputProps={{ style: { width: "100%" } }}
                menuListProps={{ style: { width: 154 } }}
                value={getLabel(colorAttribute["attribute"])}
                className={classes.attributeSelect}
            >
                <MenuItem value="None" onClick={(e: any) => setAttribute({ name: "None", index: -1 })}>
                    None
                </MenuItem>
                <MenuItem value="Label" onClick={(e: any) => setAttribute({ name: "Label", index: -2 })}>
                    Label
                </MenuItem>
                <MenuItem value="Multi" style={{ display: "none" }} disabled>
                    Multi
                </MenuItem>
                {attributes.map((attribute: any) => (
                    <MenuItem
                        key={"attribute-" + attribute.index + "-" + attribute.name}
                        value={attribute.name}
                        onClick={(e: any) => setAttribute(attribute)}
                    >
                        {attribute.label}
                    </MenuItem>
                ))}
            </CustomSelect>
            <div
                className={classes.colorBox}
                style={{ backgroundColor: colorAttribute.color }}
                onClick={(event: any) => setAnchorEl(event.target)}
            ></div>
            {anchorEl ? (
                <Menu
                    id="menu-list-grow"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    style={{ marginLeft: 5 }}
                >
                    <SketchPicker
                        className={classes.palette}
                        color={colorAttribute.color}
                        onChangeComplete={handleColorChange}
                    />
                </Menu>
            ) : (
                ""
            )}
        </Grid>
    );
}

function GeoChemAttributes(props: IDrillHoleSettings) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    var { layer, selectedLayers } = props;

    const [update, setUpdate] = useState(false);

    if (selectedLayers.length === 1) {
        layer = selectedLayers[0];
    } else {
        layer = props.layer;
    }

    const { geoShowLabels, geoLabelSize } = globalSettings[layer.ID];

    const toggleLabels = (evt: any) => {
        layer["geo_chem_show_labels"] = evt.target.checked ? "1" : "0";
        globalSettings[layer.ID]["geoShowLabels"] = evt.target.checked;
        setUpdate((prevUpdate) => !prevUpdate);
        updateGeoChemSettings(layer);
    };

    const setGeoProp = (prop: string, value: any) => {
        layer[prop] = value;
        const global: any = globalSettings[layer.ID];
        global[prop] = value;
        setUpdate((prevUpdate) => !prevUpdate);
        updateGeoChemSettings(layer);
    };

    return (
        <Section name="labels" label="Labels" style={{ userSelect: update ? "none" : "none" }}>
            <div className={common.sectionContent}>
                <div
                    className={classes.row}
                    style={{
                        marginLeft: -4,
                        marginTop: 10,
                    }}
                >
                    <Typography
                        className={classes.label}
                        style={{
                            paddingLeft: 1,
                            paddingRight: 25,
                        }}
                    >
                        Show Labels
                    </Typography>
                    <div className={classes.right}>
                        <Switch size="small" checked={geoShowLabels} onChange={toggleLabels} />
                    </div>
                </div>
                {geoShowLabels ? (
                    <div>
                        <div
                            className={classes.row}
                            style={{
                                marginLeft: -4,
                                marginTop: 10,
                                height: 40,
                            }}
                        >
                            <Typography
                                className={classes.label}
                                style={{
                                    paddingLeft: 1,
                                    paddingRight: 25,
                                }}
                            >
                                Label Size
                            </Typography>
                            <Grid
                                xs={4}
                                item={true}
                                style={{
                                    display: "inline-flex",
                                    position: "absolute",
                                    right: 10,
                                    width: 100,
                                }}
                            >
                                <DashInput
                                    name={layer.ID + "-label-size"}
                                    className={classes.numberInput}
                                    value={geoLabelSize}
                                    style={{ width: 32, height: 24, left: 9, top: 0 }}
                                    inputProps={{ min: 0.1, step: 0.1 }}
                                    onChange={(evt: any) =>
                                        setGeoProp("geo_chem_label_size", parseFloat(evt.target.value))
                                    }
                                    isLimbo={false}
                                />
                            </Grid>
                        </div>
                        <div
                            className={classes.row}
                            style={{
                                marginLeft: -4,
                                marginTop: 10,
                                height: 40,
                            }}
                        >
                            <Typography
                                className={classes.label}
                                style={{
                                    paddingLeft: 1,
                                    paddingRight: 25,
                                }}
                            >
                                Label Properties
                            </Typography>
                        </div>
                        <div
                            className={classes.row}
                            style={{
                                marginLeft: -4,
                                marginTop: 10,
                                height: 40,
                            }}
                        >
                            <Typography
                                className={classes.label}
                                style={{
                                    paddingLeft: 1,
                                    paddingRight: 25,
                                    width: 84,
                                }}
                            >
                                Top
                            </Typography>
                            <AttributeList position="top" {...props} />
                        </div>
                        <div
                            className={classes.row}
                            style={{
                                marginLeft: -4,
                                marginTop: 10,
                                height: 40,
                            }}
                        >
                            <Typography
                                className={classes.label}
                                style={{
                                    paddingLeft: 1,
                                    paddingRight: 25,
                                    width: 84,
                                }}
                            >
                                Bottom
                            </Typography>
                            <AttributeList position="bottom" {...props} />
                        </div>
                        <div
                            className={classes.row}
                            style={{
                                marginLeft: -4,
                                marginTop: 10,
                                height: 40,
                            }}
                        >
                            <Typography
                                className={classes.label}
                                style={{
                                    paddingLeft: 1,
                                    paddingRight: 25,
                                    width: 84,
                                }}
                            >
                                Left
                            </Typography>
                            <AttributeList position="left" {...props} />
                        </div>
                        <div
                            className={classes.row}
                            style={{
                                marginLeft: -4,
                                marginTop: 10,
                                height: 40,
                            }}
                        >
                            <Typography
                                className={classes.label}
                                style={{
                                    paddingLeft: 1,
                                    paddingRight: 25,
                                    width: 84,
                                }}
                            >
                                Right
                            </Typography>
                            <AttributeList position="right" {...props} />
                        </div>
                    </div>
                ) : (
                    ""
                )}
            </div>
        </Section>
    );
}

export default function DrillHoleSettings(props: IDrillHoleSettings) {
    var { layer, currentProject } = props;
    const [isGeoChem, setGeoChem] = useState(false);

    useEffect(() => {
        var source = getSourceEntity(layer, ViewerEvents.inputs, currentProject);
        if (source !== null && source["file_type"] === "GEOCHEM" && !isGeoChem) {
            setGeoChem(true);
        }
    }, [layer, currentProject, isGeoChem]);

    if (!globalSettings[layer.ID]) {
        globalSettings[layer.ID] = getDrillHoleSettings(layer);
        console.log("globalSettings:", globalSettings);
    }

    return (
        <Fragment>
            <DrillHoleFilter {...props} />
            {!isGeoChem ? <DrillHoleCylinders {...props} /> : ""}
            {!isGeoChem ? <DrillHoleCollars {...props} /> : <GeoChemCollars {...props} />}
            {!isGeoChem ? <DrillHoleAttributes {...props} /> : <GeoChemAttributes {...props} />}
        </Fragment>
    );
}
